import React, { useEffect, useState } from 'react';
import { api } from '../../api';
import { useAuth } from '../../AuthContext';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Dog, ShieldCheck, ShieldAlert, AlertTriangle, MessageSquare, GraduationCap, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function TrainerDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dogs, setDogs] = useState([]);
  const [training, setTraining] = useState([]);

  useEffect(() => {
    api.get('/trainer/dogs').then(r => setDogs(r.data));
    api.get('/training/bookings').then(r => setTraining(r.data));
  }, []);

  const now = new Date();
  const upcomingTraining = training.filter(t => new Date(t.end) >= now && t.status !== 'CANCELLED');
  const activeTraining = training.filter(t => { const s=new Date(t.start), e=new Date(t.end); return s <= now && e >= now && t.status !== 'CANCELLED'; });

  return (
    <div className='p-6 lg:p-8 max-w-7xl mx-auto'>
      <div className='flex items-center justify-between mb-6'>
        <div>
          <div className='text-[10px] uppercase tracking-[0.16em] font-bold text-[#C8102E]'>Trainer</div>
          <h1 className='font-display text-3xl font-bold text-slate-900 mt-1'>Welcome, {user?.full_name}</h1>
          <p className='text-slate-500 text-sm mt-1'>Your assigned dogs, active training programs, and upcoming sessions.</p>
        </div>
        <div className='flex gap-2'>
          <Button variant='outline' className='border-slate-200 text-slate-600 hover:bg-slate-50' onClick={()=>navigate('/admin/trainer-grid')} data-testid='trainer-view-grid'><Calendar className='w-4 h-4 mr-2' /> Schedule</Button>
          <Button className='bg-[#C8102E] hover:bg-[#A50D26] text-slate-900' onClick={() => navigate('/community')} data-testid='trainer-post-community'>
            <MessageSquare className='w-4 h-4 mr-2' /> Post to Community
          </Button>
        </div>
      </div>

      <div className='grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6'>
        {[
          {l:'Assigned dogs',v:dogs.length, color:'#1B3A87'},
          {l:'Active programs',v:activeTraining.length, color:'#C8102E'},
          {l:'Upcoming sessions',v:upcomingTraining.length, color:'#167A5A'},
          {l:'With medications',v:dogs.filter(d=>!!d.medications).length, color:'#B45309'},
        ].map((k) => (
          <Card key={k.l} className='p-5 bg-white border-slate-200'>
            <div className='text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500'>{k.l}</div>
            <div className='font-display text-3xl font-bold tabular-nums mt-2' style={{color: k.color}}>{k.v}</div>
          </Card>
        ))}
      </div>

      {/* Active / Upcoming Training */}
      <Card className='p-5 bg-white border-slate-200 mb-6'>
        <h3 className='font-display font-semibold text-lg mb-3 flex items-center gap-2'><GraduationCap className='w-5 h-5 text-[#C8102E]' /> Active & Upcoming Training</h3>
        {upcomingTraining.length === 0 ? (
          <div className='text-sm text-slate-500'>No upcoming sessions. Schedule one from the POS terminal.</div>
        ) : (
          <div className='space-y-2'>
            {upcomingTraining.slice(0, 5).map(t => (
              <div key={t.id} className='flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200' data-testid={`trainer-tb-${t.id}`}>
                <div>
                  <div className='font-semibold text-sm text-slate-900'>{t.dog_name} <span className='text-slate-500'>· {t.client_name}</span></div>
                  <div className='text-xs text-slate-500'>{t.program_label} · {new Date(t.start).toLocaleDateString()} → {new Date(t.end).toLocaleDateString()}</div>
                </div>
                <Badge className={t.status === 'PAID' ? 'bg-[#167A5A]/15 text-[#7DD3A8]' : 'bg-[#B45309]/15 text-[#FBBF24]'}>{t.status.replace('_',' ')}</Badge>
              </div>
            ))}
          </div>
        )}
      </Card>

      <h3 className='font-display font-semibold text-lg text-slate-900 mb-3'>Assigned Dogs</h3>
      {dogs.length === 0 ? (
        <Card className='p-10 text-center bg-white border-dashed border-slate-200 text-slate-500'>
          No dogs assigned yet. Self-assign from Clients & Dogs directory.
          <div className='mt-3'>
            <Button variant='outline' className='border-slate-200 text-slate-600 hover:bg-slate-50' onClick={() => navigate('/admin/clients')} data-testid='trainer-go-clients'>Browse Clients & Dogs</Button>
          </div>
        </Card>
      ) : (
        <div className='grid sm:grid-cols-2 lg:grid-cols-3 gap-4' data-testid='trainer-dogs-grid'>
          {dogs.map((d) => (
            <Card key={d.id} className='overflow-hidden bg-white border-slate-200 hover:border-[#1B3A87] transition-colors' data-testid={`trainer-dog-${d.id}`}>
              <div className='h-36 bg-slate-50 relative'>
                {d.photo_base64 ? <img src={d.photo_base64} alt={d.name} className='w-full h-full object-cover' /> :
                  <div className='w-full h-full flex items-center justify-center text-slate-500 font-display text-5xl'>{d.name[0]}</div>}
                {d.vax_compliant ? <Badge className='absolute top-3 right-3 bg-[#167A5A] text-slate-900 border-0'><ShieldCheck className='w-3 h-3 mr-1' /> VAX OK</Badge> :
                  <Badge className='absolute top-3 right-3 bg-[#B45309] text-slate-900 border-0'><ShieldAlert className='w-3 h-3 mr-1' /> VAX</Badge>}
              </div>
              <div className='p-4'>
                <div className='font-display font-bold text-lg text-slate-900'>{d.name}</div>
                <div className='text-xs text-slate-500 mt-0.5'>{d.breed || '—'} · Owner: {d.owner_name}</div>
                {d.owner_phone && <div className='text-xs text-slate-500 font-mono mt-1'>{d.owner_phone}</div>}
                {d.allergies && <div className='text-xs mt-2 text-[#F87171]'><AlertTriangle className='w-3 h-3 inline mr-1' /> {d.allergies}</div>}
                {d.medications && <div className='text-xs mt-1 text-[#FBBF24]'>Meds: {d.medications}</div>}
                {d.training_needs && <div className='text-xs mt-2 text-slate-600 line-clamp-3'><strong className='text-slate-900'>Goals:</strong> {d.training_needs}</div>}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
